/**
 * Standalone 2D exports of an assembled level: the same SVG and DXF the room
 * editor offers, for every room on the level at once. The SVG is literally
 * the assembly's own rooms layer (each room drawn by the editor's PlanSvg in
 * export mode and placed by its transform) plus the room labels, fitted into
 * an <svg> and serialized; the DXF converts that drawing through the plan
 * DXF path, which follows each room's placement transform. Rooms in the file
 * match rooms on screen stroke for stroke, showing what the view menu shows.
 *
 * Plan feet, the level's region centered at the origin, y up: the same frame
 * as the level's STEP/STL exports, so the plan overlays the 3D model.
 */

import { expandPlanBounds, type PlanBounds, WALL_THICKNESS } from '@kaiizen/kai-floor-plan';
import {
  assemblyBounds,
  type AssemblyRoom,
  assemblySceneCenter,
  createViewport,
  DEFAULT_FLOOR_PLAN_PREFERENCES,
  fitAssemblyViewport,
  type PlanViewPreferences,
} from '@kaiizen/kai-floor-plan-editor-core';
import { renderToStaticMarkup } from 'react-dom/server';
import { canvasBg } from '../../styles';
import { EXPORT_MARGIN_PX, EXPORT_PX_PER_FOOT, exportToPlan, planDxfDocument, type PlanSvgExport, svgToDxfEntities } from '../plan';
import { AssemblyRoomsLayer } from './AssemblyPlanSvg';
import { RoomLabel, roomLabelCenter } from './RoomLabel';

const EMPTY_REGION: PlanBounds = { minX: 0, minY: 0, maxX: 0, maxY: 0 };

/** The level's measurement region: the rooms' union, out to their outer wall faces. */
const exportRegion = (rooms: readonly AssemblyRoom[]): PlanBounds => expandPlanBounds(assemblyBounds(rooms) ?? EMPTY_REGION, WALL_THICKNESS);

/** The level rendered for export, plus the projection serializers need to invert it. */
export const renderAssemblyExport = (rooms: readonly AssemblyRoom[], view: PlanViewPreferences = DEFAULT_FLOOR_PLAN_PREFERENCES): PlanSvgExport => {
  const region = exportRegion(rooms);
  const width = Math.round((region.maxX - region.minX) * EXPORT_PX_PER_FOOT + 2 * EXPORT_MARGIN_PX);
  const height = Math.round((region.maxY - region.minY) * EXPORT_PX_PER_FOOT + 2 * EXPORT_MARGIN_PX);
  const viewport = fitAssemblyViewport(assemblyBounds(rooms), width, height, createViewport());
  const svg = renderToStaticMarkup(
    <svg xmlns='http://www.w3.org/2000/svg' width={width} height={height} viewBox={`0 0 ${width} ${height}`}>
      <rect width={width} height={height} fill={canvasBg} />
      <AssemblyRoomsLayer rooms={rooms} vp={viewport} stageW={width} stageH={height} layers={view.layers} />
      {rooms.map(assemblyRoom => (
        <RoomLabel key={assemblyRoom.id} assemblyRoom={assemblyRoom} view={view} center={roomLabelCenter(assemblyRoom, viewport)} selectedId={null} />
      ))}
    </svg>,
  );
  return { svg, viewport, width, height };
};

export const assemblyToPlanSvg = (rooms: readonly AssemblyRoom[], view?: PlanViewPreferences): string => renderAssemblyExport(rooms, view).svg;

/** The level's DXF, centered where editor-core centers the level's STEP and STL (`assemblySceneCenter`), so the plan overlays the 3D model. */
export const assemblyToPlanDxf = (rooms: readonly AssemblyRoom[], view?: PlanViewPreferences): string => {
  const { svg, viewport } = renderAssemblyExport(rooms, view);
  return planDxfDocument(svgToDxfEntities(svg, exportToPlan(viewport, assemblySceneCenter(rooms)), viewport.scale));
};
